import { getRowsAndTiersFromSlotKeys, getSizesFromSlots, sortRowsArray } from "@tedivo/tedivo-bay-grid-pure";
import { BayLevelEnum, ForeAftEnum, ValuesSourceEnum } from "open-vessel-definition";
import { feetToMillimeters, sortNumericAsc } from "@tedivo/tedivo-pure-helpers";
const name = "cgsValidator";
const okTranslationKey = "validators:cgs.cgsValidator.ok";
const errorTranslationKey = "validators:cgs.cgsValidator.error";
const specialScopes = [];
/** Check if all bays with 40s are paired */
export const cgsValidator = {
    name,
    okTranslationKey,
    errorTranslationKey,
    fn: cgsValidatorFn,
    specialScopes,
};
export function cgsValidatorFn({ data: { json, lcgVcgTcgAndPairings }, }) {
    const missingLCGs = [];
    const missingBulkheadLCGs = [];
    const baysData = json.baysData;
    const masterCGs = json.shipData.masterCGs || {};
    const rowsAbove = calculateRowsFromSlots(baysData.filter((bl) => bl.level === BayLevelEnum.ABOVE));
    const rowsBelow = calculateRowsFromSlots(baysData.filter((bl) => bl.level === BayLevelEnum.BELOW));
    // 1. TCGs ----------------------------------------------
    // Check only for Master TCGs
    const masterAboveTCGs = rowsAbove.reduce((acc, row) => {
        if (masterCGs.aboveTcgs[row] === undefined)
            acc.push(row);
        return acc;
    }, []);
    const masterBelowTCGs = rowsBelow.reduce((acc, row) => {
        if (masterCGs.belowTcgs[row] === undefined)
            acc.push(row);
        return acc;
    }, []);
    // 2. VCGs ----------------------------------------------
    // Check only for Master Bottom Bases
    const minBases = new Set();
    baysData.forEach((bl) => {
        const perSlotInfo = bl.perSlotInfo;
        if (perSlotInfo !== undefined) {
            const { minTier } = getRowsAndTiersFromSlotKeys(Object.keys(perSlotInfo));
            if (minTier)
                minBases.add(minTier);
        }
    });
    const masterVCGs = Array.from(minBases)
        .filter((tier) => Number(tier) < 200)
        .reduce((acc, tier) => {
        if (masterCGs.bottomBases[tier] === undefined)
            acc.push(tier);
        return acc;
    }, []);
    // 3. LCGs ----------------------------------------------
    baysData.forEach((bl) => {
        const perSlotInfo = bl.perSlotInfo;
        if (perSlotInfo !== undefined) {
            const sizes = getSizesFromSlots(perSlotInfo).sizes;
            sizes.forEach((size) => {
                var _a, _b;
                if (((_b = (_a = bl.infoByContLength) === null || _a === void 0 ? void 0 : _a[size]) === null || _b === void 0 ? void 0 : _b.lcg) === undefined) {
                    missingLCGs.push({
                        bay: bl.isoBay,
                        level: bl.level,
                        size,
                        reasons: []
                    });
                }
            });
        }
        const bulkhead = bl.bulkhead;
        if (bulkhead !== undefined) {
            if (bulkhead.aft && bulkhead.aftLcg === undefined)
                missingBulkheadLCGs.push({
                    bay: bl.isoBay,
                    level: bl.level,
                    pos: ForeAftEnum.AFT,
                });
            if (bulkhead.fore && bulkhead.foreLcg === undefined)
                missingBulkheadLCGs.push({
                    bay: bl.isoBay,
                    level: bl.level,
                    pos: ForeAftEnum.FWD,
                });
        }
    });
    const outOfBoundsResult = calculateOutOfBoundsLCGs(json, lcgVcgTcgAndPairings);
    const cgsAreKnown = json.shipData.lcgOptions.values === ValuesSourceEnum.KNOWN ||
        json.shipData.vcgOptions.values === ValuesSourceEnum.KNOWN ||
        json.shipData.tcgOptions.values === ValuesSourceEnum.KNOWN;
    const totalMissingCGs = masterAboveTCGs.length +
        masterBelowTCGs.length +
        masterVCGs.length +
        missingLCGs.length +
        missingBulkheadLCGs.length;
    const isValid = !cgsAreKnown || totalMissingCGs === 0;
    const messageTranslationKey = !cgsAreKnown
        ? "validators:cgs.cgsValidator.notNeeded"
        : totalMissingCGs === 0
            ? okTranslationKey
            : errorTranslationKey;
    return {
        name,
        isValid,
        messageTranslationKey,
        result: {
            validResults: [],
            invalidResults: [],
            summary: {
                masterAboveTCGs: masterAboveTCGs.sort(sortRowsArray),
                masterBelowTCGs: masterBelowTCGs.sort(sortRowsArray),
                masterVCGs: masterVCGs.sort(sortNumericAsc),
                missingLCGs,
                missingBulkheadLCGs,
                totalMissingCGs,
                outOfBounds: outOfBoundsResult,
            },
        },
    };
}
function calculateRowsFromSlots(baysData = []) {
    const rows = new Set();
    baysData.forEach((bl) => {
        const rowsFromSlotsInfo = bl.perSlotInfo
            ? Object.keys(bl.perSlotInfo).map((s) => s.substring(0, 2))
            : [];
        rowsFromSlotsInfo.forEach((r) => rows.add(r));
    });
    return Array.from(rows);
}
function calculateOutOfBoundsLCGs({ shipData, sizeSummary }, lcgVcgTcgAndPairings) {
    var _a;
    const flagged = new Map();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const flag = (bay, level, size, reason) => {
        const key = `${bay}-${level}-${size || "_"}`;
        const flaggedElement = flagged.get(key);
        if (!flaggedElement) {
            flagged.set(key, { bay, level, size, reasons: [reason] });
        }
        else {
            flaggedElement.reasons.push(reason);
        }
    };
    const result = {
        loaIsLowerThanLpp: false,
        loaMighBeOutOfRange: false,
        lppMighBeOutOfRange: false,
        lcgsMightBeOutOfRange: false,
        baysOutOfBounds: []
    };
    // Static comparisons
    const loa = shipData.loa;
    const lpp = shipData.lcgOptions.lpp;
    if (lpp > 0) {
        if (loa !== undefined && loa < lpp)
            result.loaIsLowerThanLpp = true;
        if (loa !== undefined && loa / lpp > 2)
            result.loaMighBeOutOfRange = true;
        if (lpp > 900000)
            result.lppMighBeOutOfRange = true;
    }
    if (!lcgVcgTcgAndPairings)
        return result;
    // LCGs when Known
    if (shipData.lcgOptions.values === ValuesSourceEnum.KNOWN) {
        const { maxLcg, minLcg } = lcgVcgTcgAndPairings;
        const lcgRange = maxLcg - minLcg;
        if (loa !== undefined && lcgRange > loa)
            result.lcgsMightBeOutOfRange = true;
        const MAX_DEVIATION_SIMILARSIZE_MM = 4000; // 10m: flag if LCG deviates more from level avg
        const MIN_PAIRING_DELTA_MM = 2000; // 40ft must differ from bay's 20ft avg by at least this
        for (const baysForLevel of [
            lcgVcgTcgAndPairings.bayLevelPositionsAbove,
            lcgVcgTcgAndPairings.bayLevelPositionsBelow,
        ]) {
            if (baysForLevel.length === 0)
                continue;
            // Pass 2: flag anomalies per bay
            for (let i = 0; i < baysForLevel.length; i++) {
                const bayPos = baysForLevel[i];
                const avgs = bayPos.getAvgCGs();
                if (!avgs)
                    continue;
                for (const size of bayPos.sizes) {
                    const lcg = (_a = avgs.perSize.get(size)) === null || _a === void 0 ? void 0 : _a.lcg;
                    if (lcg === undefined)
                        continue;
                    // Check 1: LCG deviates too much from the level average for its size group
                    const avg = size < 40 ? Math.floor(avgs.avgLcg20) : Math.floor(avgs.avgLcg40);
                    if (avg !== undefined && Math.abs(lcg - avg) > MAX_DEVIATION_SIMILARSIZE_MM) {
                        flag(bayPos.isoBay, bayPos.level, size, ReasonCgsWrong.LCG_DEVIATES_MUCH);
                        continue;
                    }
                    // Check 2: 40ft pairing direction vs the bay's own 20ft average
                    // pairedBay=AFT → 40ft extends aft → LCG must be lower than 20ft avg
                    // pairedBay=FWD → 40ft extends fwd → LCG must be higher than 20ft avg
                    if (size >= 40) {
                        switch (bayPos.pairedBay) {
                            case ForeAftEnum.AFT:
                                if (lcg > avgs.avgLcg20 - MIN_PAIRING_DELTA_MM)
                                    flag(bayPos.isoBay, bayPos.level, size, ReasonCgsWrong.LCG_DIFF_MUCH);
                                break;
                            case ForeAftEnum.FWD:
                                if (lcg < avgs.avgLcg20 + MIN_PAIRING_DELTA_MM)
                                    flag(bayPos.isoBay, bayPos.level, size, ReasonCgsWrong.LCG_DIFF_MUCH);
                                break;
                            case undefined:
                                if (Math.abs(lcg - avgs.avgLcg20) < MIN_PAIRING_DELTA_MM)
                                    // This is the case where we don't know the pairing, but there is a 40,
                                    // so we check the absolute value
                                    flag(bayPos.isoBay, bayPos.level, size, ReasonCgsWrong.LCG_DIFF_MUCH);
                                break;
                        }
                    }
                }
                // Check 3: sequential ordering — going AFT (higher bay index) LCGs must decrease
                // baysForLevel is sorted ascending by bay number; bay 001 (FWD) has the highest LCG
                if (i > 0) {
                    const prevAvgs = baysForLevel[i - 1].getAvgCGs();
                    if (prevAvgs &&
                        prevAvgs.avgLcg20 !== undefined &&
                        prevAvgs.avgLcg20 !== 0 &&
                        avgs.avgLcg20 !== undefined &&
                        avgs.avgLcg20 !== 0 &&
                        avgs.avgLcg20 > prevAvgs.avgLcg20) {
                        for (const size of bayPos.sizes.filter((s) => s < 40)) {
                            flag(bayPos.isoBay, bayPos.level, size, ReasonCgsWrong.LCG_GROWS_WRONG_DIR);
                        }
                    }
                }
            }
        }
    }
    if (shipData.tcgOptions.values === ValuesSourceEnum.KNOWN) {
        const { centerLineRow, maxRow } = sizeSummary;
        if (maxRow === undefined)
            return;
        const TCG_IN_BETWEEN_MARGIN = 3; //FEET
        for (const baysForLevel of [
            lcgVcgTcgAndPairings.bayLevelPositionsAbove,
            lcgVcgTcgAndPairings.bayLevelPositionsBelow,
        ]) {
            if (baysForLevel.length === 0)
                continue;
            // Pass 2: flag anomalies per bay
            for (let i = 0; i < baysForLevel.length; i++) {
                const bayPos = baysForLevel[i];
                const avgs = bayPos.getAvgCGs();
                if (!avgs)
                    continue;
                // Wrong Sign in TCGs
                if (bayPos.minTcg > bayPos.maxTcg)
                    flag(bayPos.isoBay, bayPos.level, undefined, ReasonCgsWrong.TCG_WRONG_SIGN);
                const theoreticalTcgRange = ((centerLineRow ? 1 : 0) + maxRow) * feetToMillimeters(8 + TCG_IN_BETWEEN_MARGIN);
                // Too muich difference (accounting for a margin)
                if (bayPos.minTcg < -theoreticalTcgRange * 0.5)
                    flag(bayPos.isoBay, bayPos.level, undefined, ReasonCgsWrong.TCG_DIFF_MUCH);
                if (bayPos.minTcg > theoreticalTcgRange * 0.5)
                    flag(bayPos.isoBay, bayPos.level, undefined, ReasonCgsWrong.TCG_DIFF_MUCH);
                // Check if TCGs are progressive from Port->Stbd
                let prevTcg = undefined;
                const rowsInBay = Array.from((bayPos.perRowAverageBayTcg).keys()).sort(sortRowsArray);
                rowsInBay.forEach(row => {
                    if (prevTcg === undefined) {
                        prevTcg = bayPos.perRowAverageBayTcg.get(row);
                        return;
                    }
                    const currTcg = bayPos.perRowAverageBayTcg.get(row);
                    if (currTcg !== undefined && prevTcg > currTcg)
                        flag(bayPos.isoBay, bayPos.level, undefined, ReasonCgsWrong.TCG_WRONG_PROGRESSION);
                });
            }
        }
    }
    if (shipData.vcgOptions.values === ValuesSourceEnum.KNOWN) {
        //TODO
    }
    result.baysOutOfBounds = Array.from(flagged.values());
    return result;
}
export var ReasonCgsWrong;
(function (ReasonCgsWrong) {
    ReasonCgsWrong["LCG_DEVIATES_MUCH"] = "LCG_DEVIATES_MUCH";
    ReasonCgsWrong["LCG_GROWS_WRONG_DIR"] = "LCG_GROWS_WRONG_DIR";
    ReasonCgsWrong["LCG_DIFF_MUCH"] = "LCG_DIFF_MUCH";
    ReasonCgsWrong["TCG_DIFF_MUCH"] = "TCG_DIFF_MUCH";
    ReasonCgsWrong["TCG_WRONG_SIGN"] = "TCG_WRONG_SIGN";
    ReasonCgsWrong["TCG_WRONG_PROGRESSION"] = "TCG_WRONG_PROGRESSION";
})(ReasonCgsWrong || (ReasonCgsWrong = {}));
